import React, { useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { SidebarDrawer } from "@jugl-web/ui-components/web";
import { Button, DateTimeInput } from "@jugl-web/ui-components/cross-platform";
import { ChatMessagePayload } from "@jugl-web/rest-api";
import { v4 as uuidv4 } from "uuid";
import { useActiveChat } from "@web-src/modules/chats/hooks/useActiveChat";
import { format } from "date-fns";
import { useLanguage } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import { TextAreaInput } from "@jugl-web/ui-components/cross-platform/forms/TextAreaInput";

const MAX_VIDEO_MEETING_NAME_LENGTH = 100;

export const ChatAddConferenceSidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  setConference: (conference: ChatMessagePayload["conference"]) => void;
}> = ({ isOpen, onClose, setConference }) => {
  const { t } = useTranslations();
  const [name, setName] = useState<string>("");
  const [scheduleDate, setScheduleDate] = useState<string>();
  const { dateLocale } = useLanguage();

  const { chat } = useActiveChat();
  const handleSetConference = async (instant?: boolean) => {
    if (!chat) {
      return;
    }
    const instantConference = {
      channel: uuidv4(),
      name,
    };
    const date = scheduleDate && new Date(scheduleDate);
    const scheludedConference = {
      ...instantConference,
      date: date && format(date, "MMM d, yyyy", { locale: dateLocale }),
      time: date && format(date, "pp", { locale: dateLocale }),
    };
    setConference(instant ? instantConference : scheludedConference);
    handleClose();
  };

  const handleClose = () => {
    setName("");
    setScheduleDate(undefined);
    onClose();
  };

  return (
    <SidebarDrawer
      className="width-[480px]"
      title={t({
        id: "chats-page.add-conference-sidebar-title",
        defaultMessage: "Start Meeting",
      })}
      onClose={handleClose}
      isOpen={isOpen}
    >
      <SidebarDrawer.Content>
        <div className="px-8 pb-12 pt-8">
          <Button
            fullWidth
            onClick={() => handleSetConference(true)}
            className="h-10"
          >
            {t({
              id: "chats-page.add-conference-sidebar-instant-button",
              defaultMessage: "Start an Instant Meeting",
            })}
          </Button>
        </div>
        <div className="h-3 w-full bg-[#ECEFF0]" />
        <div className="flex flex-col gap-6 px-8 pt-6">
          <span className="leading-3 text-[#828282]">
            {t({
              id: "chats-page.add-conference-sidebar-or-schedule-a-meeting",
              defaultMessage: "Or Schedule a Meeting",
            })}
          </span>
          <TextAreaInput
            label={t({
              id: "chats-page.add-conference-sidebar-meeting-name",
              defaultMessage: "Meeting name",
            })}
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            isRequired
            value={name}
            onChange={(e) => setName(e.target.value.trimStart())}
            maxLength={MAX_VIDEO_MEETING_NAME_LENGTH}
            lengthIndicator={{
              max: MAX_VIDEO_MEETING_NAME_LENGTH,
            }}
            isInvalid={name.length >= MAX_VIDEO_MEETING_NAME_LENGTH}
          />
          <DateTimeInput
            label={t({
              id: "chats-page.date-and-time",
              defaultMessage: "Date & Time",
            })}
            onChange={setScheduleDate}
            isRequired
            value={scheduleDate}
            minDate={new Date()}
            showTimePicker
          />
          {scheduleDate && (
            <Button
              fullWidth
              onClick={() => handleSetConference()}
              className="h-10"
            >
              {t({
                id: "chats-page.add-conference-sidebar-schedule-meeting",
                defaultMessage: "Schedule a Meeting",
              })}
            </Button>
          )}
        </div>
      </SidebarDrawer.Content>
    </SidebarDrawer>
  );
};
